'use client'

import { useCallback, useMemo, useState } from 'react'
import { flow, get, orderBy } from 'lodash'

import { ListSortDirection } from '@docpace/shared-ts-utils'

// Structural shape of the static sort values (DepartmentSort, ManagerSort, etc.)
// used by the useSort* hooks — not all of them share a base class.
export interface ListSortValue {
    sortPath: string
    defaultSort: ListSortDirection
    flowFuncs?: ((value: any) => any)[]
}

interface UseSortListItemsProps<T, SortKey extends string> {
    items: T[]
    sortValues: { [Key in SortKey]: ListSortValue }
    initialSortKey: SortKey
    initialSortDirection: ListSortDirection
    initialSecondarySortKey?: SortKey
    initialSecondarySortDirection?: ListSortDirection
}

export interface UseSortListItemsOutput<T, SortKey extends string> {
    sortByKey: SortKey
    sortDirection: ListSortDirection
    secondarySortByKey?: SortKey
    secondarySortByDirection?: ListSortDirection
    reverseSortDirection: () => void
    setSortDirection: (sortDirection: ListSortDirection) => void
    setSortByKey: (sortByKey: SortKey) => void
    setSecondarySortByKey: (sortByKey: SortKey) => void
    setSecondarySortByDirection: (sortDirection: ListSortDirection) => void
    sortedItems: T[]
}

const makeIteratee = <T,>(sortValue: ListSortValue) => {
    const transformValue = flow(sortValue.flowFuncs ?? [])
    return (item: T) => transformValue(get(item, sortValue.sortPath))
}

const toOrder = (direction: ListSortDirection) =>
    direction === ListSortDirection.DESC ? 'desc' : 'asc'

const reverseDirection = (direction: ListSortDirection) =>
    direction === ListSortDirection.ASC
        ? ListSortDirection.DESC
        : ListSortDirection.ASC

function sortListItems<T>(
    items: T[],
    sortValue: ListSortValue,
    sortDirection: ListSortDirection,
    secondarySortValue?: ListSortValue,
    secondarySortByDirection?: ListSortDirection
): T[] {
    const iteratees: ((item: T) => unknown)[] = [makeIteratee<T>(sortValue)]
    const orders: ('asc' | 'desc')[] = [toOrder(sortDirection)]

    if (
        secondarySortValue &&
        secondarySortValue.sortPath !== sortValue.sortPath
    ) {
        iteratees.push(makeIteratee<T>(secondarySortValue))
        orders.push(
            toOrder(secondarySortByDirection ?? secondarySortValue.defaultSort)
        )
    }

    return orderBy(items, iteratees, orders)
}

export function useSortListItems<T, SortKey extends string>({
    items,
    sortValues,
    initialSortKey,
    initialSortDirection,
    initialSecondarySortKey,
    initialSecondarySortDirection,
}: UseSortListItemsProps<T, SortKey>): UseSortListItemsOutput<T, SortKey> {
    const [sortByKey, setSortByKeyState] = useState<SortKey>(initialSortKey)
    const [sortDirection, setSortDirection] =
        useState<ListSortDirection>(initialSortDirection)
    const [secondarySortByKey, setSecondarySortByKeyState] = useState<
        SortKey | undefined
    >(initialSecondarySortKey)
    const [secondarySortByDirection, setSecondarySortByDirection] = useState<
        ListSortDirection | undefined
    >(initialSecondarySortDirection)

    const reverseSortDirection = useCallback(() => {
        setSortDirection((currentDirection) =>
            reverseDirection(currentDirection)
        )
    }, [])

    const setSortByKey = useCallback(
        (newSortByKey: SortKey) => {
            if (newSortByKey === sortByKey) {
                reverseSortDirection()
                return
            }

            setSortByKeyState(newSortByKey)
            setSortDirection(
                sortValues[newSortByKey]?.defaultSort ?? ListSortDirection.ASC
            )
        },
        [reverseSortDirection, sortByKey, sortValues]
    )

    const setSecondarySortByKey = useCallback(
        (newSortByKey: SortKey) => {
            const defaultDirection = sortValues[newSortByKey].defaultSort

            if (newSortByKey === secondarySortByKey) {
                if (
                    (secondarySortByDirection ?? defaultDirection) !==
                    defaultDirection
                ) {
                    setSecondarySortByKeyState(undefined)
                    setSecondarySortByDirection(undefined)
                    return
                }

                setSecondarySortByDirection((currentDirection) =>
                    reverseDirection(currentDirection ?? defaultDirection)
                )
                return
            }

            setSecondarySortByKeyState(newSortByKey)
            setSecondarySortByDirection(defaultDirection)
        },
        [secondarySortByDirection, secondarySortByKey, sortValues]
    )

    const sortedItems = useMemo(() => {
        const sortValue = sortValues[sortByKey]
        if (!sortValue) {
            return items
        }

        const secondarySortValue =
            secondarySortByKey && secondarySortByKey !== sortByKey
                ? sortValues[secondarySortByKey]
                : undefined

        return sortListItems(
            items,
            sortValue,
            sortDirection,
            secondarySortValue,
            secondarySortByDirection
        )
    }, [
        items,
        secondarySortByDirection,
        secondarySortByKey,
        sortByKey,
        sortDirection,
        sortValues,
    ])

    return {
        sortByKey,
        sortDirection,
        secondarySortByKey,
        secondarySortByDirection,
        reverseSortDirection,
        setSortDirection,
        setSortByKey,
        setSecondarySortByKey,
        setSecondarySortByDirection,
        sortedItems,
    }
}

export default useSortListItems
